/**
 * Browser-side Gemini API calls for Vault/BYOK users.
 * All calls go directly from browser → Google. 1895 servers never see the content.
 */
import { getGeminiApiKey, getSecurityTier } from './vaultService';

const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';

/**
 * Check if BYOK mode is active (vault tier + API key present)
 */
export function isByokActive() {
  return getSecurityTier() === 'vault' && !!getGeminiApiKey();
}

/**
 * Send a text-only request to Gemini (for analysis, script formatting, etc.)
 */
export async function geminiText(systemPrompt, userPrompt, options = {}) {
  const apiKey = getGeminiApiKey();
  if (!apiKey) throw new Error('No Gemini API key configured');

  const model = options.model || 'gemini-2.0-flash';
  const url = `${GEMINI_API_BASE}/${model}:generateContent?key=${apiKey}`;

  const body = {
    system_instruction: { parts: [{ text: systemPrompt }] },
    contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
    generationConfig: {
      temperature: options.temperature ?? 0.7,
      maxOutputTokens: options.maxTokens ?? 8192,
    },
  };

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gemini API error: ${resp.status}`);
  }

  const data = await resp.json();
  return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
}

/**
 * Send a multimodal request (text + reference images in, text + generated image out).
 * Used for storyboard frame generation, character sheets, etc.
 */
export async function geminiMultimodal(systemPrompt, userPrompt, referenceImages = [], options = {}) {
  const apiKey = getGeminiApiKey();
  if (!apiKey) throw new Error('No Gemini API key configured');

  const model = options.model || 'gemini-2.0-flash-preview-image-generation';
  const url = `${GEMINI_API_BASE}/${model}:generateContent?key=${apiKey}`;

  // Build parts: reference images first, then text prompt
  const parts = [];
  for (const img of referenceImages) {
    parts.push({
      inline_data: {
        mime_type: img.mimeType || 'image/png',
        data: img.base64Data,
      },
    });
  }
  parts.push({ text: userPrompt });

  const body = {
    system_instruction: { parts: [{ text: systemPrompt }] },
    contents: [{ role: 'user', parts }],
    generationConfig: {
      temperature: options.temperature ?? 0.7,
      maxOutputTokens: options.maxTokens ?? 8192,
      responseModalities: ['TEXT', 'IMAGE'],
    },
  };

  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error?.message || `Gemini API error: ${resp.status}`);
  }

  const data = await resp.json();
  const responseParts = data.candidates?.[0]?.content?.parts || [];

  let text = '';
  const images = [];

  for (const part of responseParts) {
    if (part.text) {
      text += part.text;
    } else if (part.inline_data?.data) {
      images.push({ data: part.inline_data.data });
    }
  }

  return { text, images };
}
